import { Injectable } from '@angular/core';
import { Observable } from 'rxjs/Observable';
import { Subject } from 'rxjs/Subject';
import 'rxjs/add/observable/of';
import 'rxjs/add/operator/switchMap';
import 'rxjs/add/operator/map';
import 'rxjs/add/operator/do';

import {
  SkillSpecializationListItem,
  SkillSpecializationResponse,
  SkillSpecializationCreateRequest,
  CuratorAssignRequest,
  TheoryLevelRequest,
  PracticeLevelRequest,
  SkillAvailableCurator,
  SkillPromptTemplateResponse,
} from '../models/skill-learning.model';
import { SkillSpecializationApiService } from './skill-specialization-api.service';
import { SkillGroupApiService } from './skill-group-api.service';
import { SkillRoleService } from './skill-role.service';

const USER_STORAGE_KEY = 'User';

@Injectable()
export class SkillSpecializationFacadeService {

  private specializationSaved$ = new Subject<void>();

  readonly onSpecializationSaved$: Observable<void> = this.specializationSaved$.asObservable();

  constructor(
    private specializationApi: SkillSpecializationApiService,
    private groupApi: SkillGroupApiService,
    private roleService: SkillRoleService,
  ) {}

  fetchSpecializationList(): Observable<SkillSpecializationListItem[]> {
    if (this.roleService.isHeadOrAdmin()) {
      return this.specializationApi.fetchAllSpecializations();
    }
    return this.specializationApi.fetchMyCuratedSpecializations();
  }

  fetchSpecializationDetail(id: string): Observable<SkillSpecializationResponse> {
    return this.specializationApi.fetchSpecializationById(id);
  }

  fetchTheoryDirectionsPrompt(id: string): Observable<SkillPromptTemplateResponse> {
    return this.specializationApi.fetchTheoryDirectionsPrompt(id);
  }

  fetchAvailableCurators(specializationId: string): Observable<SkillAvailableCurator[]> {
    const groupId = this.resolveCurrentUserGroupId();
    return this.groupApi.fetchAvailableCurators(groupId, specializationId);
  }

  fetchAllCuratorsForEdit(): Observable<SkillAvailableCurator[]> {
    const groupId = this.resolveCurrentUserGroupId();
    return this.groupApi.fetchAllCuratorsForGroup(groupId);
  }

  createSpecialization(
    request: SkillSpecializationCreateRequest,
    curatorIds: string[],
  ): Observable<SkillSpecializationResponse> {
    const createRequest: SkillSpecializationCreateRequest = Object.assign({}, request, {
      curatorIds: curatorIds || [],
    });
    return this.specializationApi.createSpecialization(createRequest);
  }

  updateGeneralInfo(id: string, request: SkillSpecializationCreateRequest): Observable<SkillSpecializationResponse> {
    return this.specializationApi.updateSpecialization(id, request)
      .do(() => this.specializationSaved$.next());
  }

  updateSpecializationWithCurators(
    id: string,
    request: SkillSpecializationCreateRequest,
    curatorIds: string[],
  ): Observable<void> {
    return this.specializationApi.updateSpecialization(id, request)
      .switchMap(() => {
        if (this.roleService.isHeadOrAdmin()) {
          const assignRequest: CuratorAssignRequest = { curatorIds };
          return this.specializationApi.assignCurators(id, assignRequest);
        }
        return Observable.of(null);
      })
      .do(() => this.specializationSaved$.next())
      .map(() => null);
  }

  assignCurators(id: string, curatorIds: string[]): Observable<void> {
    const assignRequest: CuratorAssignRequest = { curatorIds };
    return this.specializationApi.assignCurators(id, assignRequest)
      .do(() => this.specializationSaved$.next());
  }

  removeCurator(id: string, curatorId: string): Observable<void> {
    return this.specializationApi.removeCurator(id, curatorId)
      .do(() => this.specializationSaved$.next());
  }

  updateTheoryLevel(id: string, request: TheoryLevelRequest): Observable<void> {
    return this.specializationApi.updateTheoryLevel(id, request)
      .do(() => this.specializationSaved$.next());
  }

  updatePracticeLevel2(id: string, request: PracticeLevelRequest): Observable<void> {
    return this.specializationApi.updatePracticeLevel2(id, request)
      .do(() => this.specializationSaved$.next());
  }

  updatePracticeLevel3(id: string, request: PracticeLevelRequest): Observable<void> {
    return this.specializationApi.updatePracticeLevel3(id, request)
      .do(() => this.specializationSaved$.next());
  }

  uploadLevelDocument(id: string, level: number, file: File): Observable<string> {
    return this.specializationApi.uploadLevelDocument(id, level, file)
      .do(() => this.specializationSaved$.next());
  }

  buildDocumentDownloadUrl(id: string, level: number): string {
    return this.specializationApi.buildLevelDocumentDownloadUrl(id, level);
  }

  deleteSpecialization(id: string): Observable<void> {
    return this.specializationApi.deactivateSpecialization(id);
  }

  createFillTask(id: string): Observable<SkillSpecializationResponse> {
    return this.specializationApi.createFillTask(id)
      .do(() => this.specializationSaved$.next());
  }

  private resolveCurrentUserGroupId(): string {
    const raw = localStorage.getItem(USER_STORAGE_KEY);
    if (!raw) {
      return '';
    }
    const user = JSON.parse(raw);
    if (user && user.mainGroup) {
      return user.mainGroup;
    }
    return '';
  }
}



// WEBPACK FOOTER //
// C:/work/internal-frontend/src/app/content-page/common/skill-learning/services/skill-specialization-facade.service.ts